import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useSensor, useSensors } from '@dnd-kit/core';
import { SmartPointerSensor } from './dndSensors';
import {
    applyTaskMove,
    buildTaskReorderPayload,
    getAncestorIds,
    getDropIntentMetrics,
    getInheritedTimeline,
    normalizeParentId,
    resolveDropIntent,
} from '../../utils/taskHierarchyUtils';
import {
    ChartMultiple20Regular,
    Filter20Regular,
    ArrowSort20Regular,
    Grid20Regular,
    EyeOff20Regular,
    ChevronDown20Regular,
    ChevronDown12Regular,
    Delete16Regular,
    TaskListAdd20Regular,
    TaskListSquareAdd20Regular,
    GridKanban20Regular,
    TaskListSquareRtl20Regular,
    TaskListSquareLtr20Regular,
    TaskListSquareLtr24Regular,
} from '@fluentui/react-icons';

import { Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, MenuDivider } from '@fluentui/react-components';
import styles from './css/index.module.css';
import {
    GetTask,
    PatchTask,
    DeleteBulkTask,
    GetProjectTaskWorkstreams,
    GetProjectTaskTags,
    GetProjectTaskPhases,
    ReorderTasks,
    BulkUpdateTasks,
    DownloadCSVTemplate,
    UploadCSVFile,
    ImportTechDDTasks,
    ExportTasksCSV,
} from '../../redux/taskSlice';
import { GetWorkstreams } from '../../redux/dataroomSlice';
import { GetCurrentProjectData } from '../../redux/projectDataSlice';
import { toast } from 'react-toastify';
import { GetProjectUsers } from '../../redux/teamSlice';
import { ensurePulseProjectActive } from '../../utils/pulseDeepLinkProject';

import TaskDetailsSidebar from './TaskDetailsSidebar';
import ShareUpdateSidebar from './ShareUpdateSidebar';
import 'react-datepicker/dist/react-datepicker.css';
import Reporting from './Reporting';
import TasksTable from './TasksTable';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import WarningConfirmModal from '../CommonComponents/DoubleConfirmationModal/WarningConfirmModal';
import RecalculateScheduleModal from '../CommonComponents/DoubleConfirmationModal/RecalculateScheduleModal';
import AdjustDeadlineModal from '../CommonComponents/DoubleConfirmationModal/AdjustDeadlineModal';
import PauseTaskConfirmModal from '../CommonComponents/DoubleConfirmationModal/PauseTaskConfirmModal';
import { formatDuration, isSchedulingMode, normalizeTaskStatus } from '../../utils/taskSchedulingUtils';
import {
    requestBulkStatusScheduleMutation,
    requestBulkDeadlineScheduleMutation,
    requestDeadlineMutation,
    requestReorderScheduleMutation,
    requestScheduleMutation,
    requestStatusScheduleMutation,
    shouldConfirmPauseCancelResume,
    statusNeedsSchedulePreview,
    statusPatchBody,
    isCompletedStatus,
    showScheduleError,
} from '../../utils/taskScheduleMutation';
import BulkOwnerUpdateMenu from './BulkOwnerUpdateMenu';
import BulkPriorityUpdateMenu from './BulkPriorityUpdateMenu';
import BulkWorkstreamUpdateMenu from './BulkWorkstreamUpdateMenu';
import BulkTagUpdateMenu from './BulkTagUpdateMenu';
import BulkStatusUpdateMenu from './BulkStatusUpdateMenu';
import BulkDeadlineUpdateMenu from './BulkTimelineUpdateMenu';
import { setGroupByColumn, setGroupBySort, setCollapsedGroups, clearGroupBySettings, loadGroupBySettings } from '../../redux/taskSlice';
import TagFilterMenu from './TagFilterMenu';
import { DEFAULT_GROUP_BY_COLUMN, getGroupBySettings, saveGroupBySettings } from '../../utils/groupByPersistence';
import { exportTasksToPdf } from '../../utils/exportTasksPdf';
import { persistTaskTableColumnVisibility, readTaskTableColumnOrder, readTaskTableColumnVisibility } from '../../utils/taskTableColumns';

// Minimal CSV header parser to avoid pulling papaparse (and its Node stream dependency) into the bundle.
const parseCsvHeader = line => {
    const headers = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
        const ch = line[i];
        if (ch === '"') {
            if (inQuotes && line[i + 1] === '"') {
                current += '"';
                i += 1;
            } else {
                inQuotes = !inQuotes;
            }
        } else if (ch === ',' && !inQuotes) {
            headers.push(current);
            current = '';
        } else {
            current += ch;
        }
    }
    headers.push(current);
    return headers.map(h => (h || '').toString().trim());
};

const TaskManagement = () => {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const { taskData, uploadCSVBtnLoading, exportCSVLoading, groupBySettings, projectPhases } = useSelector(state => state.task);
    const [exportPDFLoading, setExportPDFLoading] = React.useState(false);
    const { selectedProject } = useSelector(state => state.projectData);
    const { projectUsersAll } = useSelector(state => state.team);
    const [filteredData, setFilteredData] = React.useState(taskData || []);

    // Hold briefly to drag a row; quick click still edits the name.
    // Inputs are ignored so typing is not captured by the drag sensor.
    const sensors = useSensors(
        useSensor(SmartPointerSensor, {
            activationConstraint: {
                delay: 200,
                tolerance: 8,
            },
        }),
    );
    const [modalVisible, setModalVisible] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState(null);

    const [showReporting, setShowReporting] = React.useState(() => !new URLSearchParams(window.location.search).get('taskId'));
    const [isAddingNewTask, setIsAddingNewTask] = React.useState(true);
    const [newTaskText, setNewTaskText] = React.useState('');
    const [filters, setFilters] = React.useState([{ column: '', value: '', id: Date.now() }]);
    const [sortColumn, setSortColumn] = React.useState('');
    const [sortOrder, setSortOrder] = React.useState('');
    const [showAllColumns, setShowAllColumns] = React.useState(() => readTaskTableColumnVisibility().showAllColumns);
    const [hiddenColumns, setHiddenColumns] = React.useState(() => readTaskTableColumnVisibility().hiddenColumns);
    const [sidebarOpen, setSidebarOpen] = React.useState(false);
    const [selectedTaskForSidebar, setSelectedTaskForSidebar] = React.useState(null);
    const [sidebarFocusRequest, setSidebarFocusRequest] = React.useState(null);
    const [selectedRows, setSelectedRows] = React.useState([]);
    const [shareUpdateOpen, setShareUpdateOpen] = React.useState(false);
    const [tableCollapsed, setTableCollapsed] = React.useState(false);
    const [expandedTasks, setExpandedTasks] = React.useState(new Set());
    const [customOrder, setCustomOrder] = React.useState(null);
    const [addNewTaskFocusTrigger, setAddNewTaskFocusTrigger] = React.useState(0);
    const [dropIntent, setDropIntent] = React.useState(null);
    const [activeDragId, setActiveDragId] = React.useState(null);
    const [pendingHierarchyMove, setPendingHierarchyMove] = React.useState(null);
    const [hierarchyMoveLoading, setHierarchyMoveLoading] = React.useState(false);
    const [pendingScheduleChange, setPendingScheduleChange] = React.useState(null);
    const [pendingDeadlineChange, setPendingDeadlineChange] = React.useState(null);
    const [deadlineChangeLoading, setDeadlineChangeLoading] = React.useState(false);
    const [scheduleChangeLoading, setScheduleChangeLoading] = React.useState(false);
    const [pendingStatusChange, setPendingStatusChange] = React.useState(null);
    const [statusChangeLoading, setStatusChangeLoading] = React.useState(false);
    const [previewTaskId, setPreviewTaskId] = React.useState(null);
    const [pendingBulkStatusChange, setPendingBulkStatusChange] = React.useState(null);
    const [bulkStatusChangeLoading, setBulkStatusChangeLoading] = React.useState(false);
    const dragPointerRef = React.useRef({ startX: 0, startY: 0, x: 0, y: 0 });
    const dropIntentRef = React.useRef(null);
    const dragIntentFrameRef = React.useRef(null);
    const taskMapRef = React.useRef(new Map());

    React.useEffect(() => {
        taskMapRef.current = new Map((taskData || []).map(task => [String(task.id), task]));
    }, [taskData]);

    React.useEffect(() => {
        persistTaskTableColumnVisibility({ showAllColumns, hiddenColumns });
    }, [showAllColumns, hiddenColumns]);

    // CSV import column mapping state
    const [csvFileForMapping, setCsvFileForMapping] = React.useState(null);
    const [csvHeaders, setCsvHeaders] = React.useState([]);
    const [csvMapping, setCsvMapping] = React.useState({
        task: '',
        mode: '',
        duration: '',
        start_date: '',
        end_date: '',
        deadline: '',
        status: '',
        tag_repr: '',
        workstream_repr: '',
        project_phase: '',
        task_id: '',
        parent_task_id: '',
    });

    // Use Redux state for group-by settings with fallback defaults
    // Treat legacy 'tag' grouping as disabled so Tag is no longer a group-by option.
    const rawGroupByColumn = groupBySettings?.column || '';
    const groupByColumn = rawGroupByColumn === 'tag' ? '' : rawGroupByColumn;
    const groupBySort = groupBySettings?.sort || '';
    const collapsedGroups = new Set(groupBySettings?.collapsedGroups || []);

    // Load group-by settings when component mounts or project changes
    React.useEffect(() => {
        if (selectedProject?.id) {
            const savedSettings = getGroupBySettings(selectedProject.id);
            if (savedSettings) {
                dispatch(loadGroupBySettings(savedSettings));
            } else {
                dispatch(loadGroupBySettings({ column: DEFAULT_GROUP_BY_COLUMN, sort: '', collapsedGroups: [] }));
            }
        }
    }, [selectedProject?.id, dispatch]);

    // Save group-by settings when they change
    React.useEffect(() => {
        if (selectedProject?.id && groupBySettings && (groupByColumn || groupBySort || groupBySettings.collapsedGroups?.length > 0)) {
            saveGroupBySettings(selectedProject.id, groupBySettings);
        }
    }, [groupByColumn, groupBySort, groupBySettings, groupBySettings?.collapsedGroups, selectedProject?.id]);

    // Update filteredData when taskData, filters, or sort changes
    React.useEffect(() => {
        if (taskData) {
            // Apply filters to data
            let filteredTasks = applyFilters([...taskData]);

            // Apply custom order if it exists, otherwise apply sorting
            if (customOrder) {
                const taskById = new Map(filteredTasks.map(task => [task.id, task]));
                const seen = new Set();
                const orderedTasks = [];

                customOrder.forEach(taskId => {
                    const task = taskById.get(taskId);
                    if (task) {
                        orderedTasks.push(task);
                        seen.add(taskId);
                    }
                });

                filteredTasks.forEach(task => {
                    if (!seen.has(task.id)) {
                        orderedTasks.push(task);
                    }
                });
                filteredTasks = orderedTasks;
            } else {
                // Apply custom sorting if specified
                filteredTasks = applySorting(filteredTasks);
            }

            // Apply grouping if specified
            const groupedResult = applyGrouping(filteredTasks);
            setFilteredData(groupedResult);
        } else {
            setFilteredData([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskData, filters, sortColumn, sortOrder, groupByColumn, groupBySort, customOrder]);
    React.useEffect(() => {
        if (selectedProject?.id) {
            dispatch(GetCurrentProjectData());
        }
    }, [selectedProject?.id, dispatch]);

    React.useEffect(() => {
        if (selectedProject) {
            dispatch(GetTask());
            dispatch(GetProjectUsers(selectedProject?.id));
            dispatch(GetProjectTaskWorkstreams());
            dispatch(GetProjectTaskTags());
            dispatch(GetProjectTaskPhases());
            dispatch(GetWorkstreams());
        }
    }, [selectedProject, dispatch]);

    // Pulse email links set localStorage.project_id before selectedProject updates — sync then load tasks.
    React.useEffect(() => {
        const taskId = searchParams.get('taskId');
        if (!taskId) {
            return;
        }

        setShowReporting(false);

        const storedProjectId = localStorage.getItem('project_id');
        if (!storedProjectId) {
            return;
        }
        if (selectedProject?.id && String(selectedProject.id) === String(storedProjectId)) {
            return;
        }

        let cancelled = false;
        (async () => {
            const ok = await ensurePulseProjectActive(dispatch, storedProjectId);
            if (cancelled || !ok) {
                if (!cancelled) {
                    toast.warn('Could not open the linked task — wrong project or not signed in to Lens.');
                }
                return;
            }
            dispatch(GetTask());
            dispatch(GetProjectUsers(storedProjectId));
            dispatch(GetProjectTaskWorkstreams());
            dispatch(GetProjectTaskTags());
            dispatch(GetProjectTaskPhases());
            dispatch(GetWorkstreams());
        })();

        return () => {
            cancelled = true;
        };
    }, [searchParams, selectedProject?.id, dispatch]);

    React.useEffect(() => {
        const taskId = searchParams.get('taskId');
        if (!taskId || !taskData?.length) return;

        const task = taskData.find(item => String(item.id) === String(taskId));
        if (!task) return;

        setSelectedTaskForSidebar(task);
        setSidebarOpen(true);

        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('taskId');
        setSearchParams(nextParams, { replace: true });
    }, [taskData, searchParams, setSearchParams]);

    const handleDeleteSelected = async () => {
        if (selectedRows.length === 0) {
            toast.warning('No tasks selected for deletion');
            return;
        }

        try {
            // Use bulk delete API instead of individual deletes
            await dispatch(DeleteBulkTask({ ids: selectedRows }));
            toast.success(`Successfully deleted ${selectedRows.length} task${selectedRows.length > 1 ? 's' : ''}`);
            setSelectedRows([]);
        } catch (error) {
            console.error('Failed to delete tasks:', error);
            toast.error('Failed to delete tasks');
        }
    };

    const handleBulkOwnerUpdate = (user, updatedCount) => {
        // Handle bulk owner update completion
        if (user) {
            toast.success(`Successfully assigned ${updatedCount} task${updatedCount > 1 ? 's' : ''} to ${user.value.full_name}`);
        } else {
            toast.success(`Successfully unassigned ${updatedCount} task${updatedCount > 1 ? 's' : ''}`);
        }
        // Refresh the project data to show updated assignments
    };

    const handleBulkPriorityUpdate = (priority, updatedCount) => {
        // Handle bulk priority update completion
        const priorityText = priority === '' ? 'None' : priority.charAt(0) + priority.slice(1).toLowerCase();
        toast.success(`Successfully set priority to ${priorityText} for ${updatedCount} task${updatedCount > 1 ? 's' : ''}`);
        // Refresh the project data to show updated priorities
    };

    const handleBulkWorkstreamUpdate = (workstream, updatedCount) => {
        // Handle bulk workstream update completion
        toast.success(`Successfully set workstream to ${workstream.name} for ${updatedCount} task${updatedCount > 1 ? 's' : ''}`);
        // Refresh the project data to show updated workstreams
    };

    const handleBulkTagUpdate = (tag, updatedCount) => {
        // Handle bulk tag update completion
        toast.success(`Successfully set tag to ${tag.name} for ${updatedCount} task${updatedCount > 1 ? 's' : ''}`);
        // Refresh the project data to show updated tags
    };

    const handleBulkStatusUpdate = (status, updatedCount) => {
        const statusText =
            status === 'COMPLETED'
                ? 'Completed'
                : status === 'WORKING_ON_IT'
                  ? 'Working on it'
                  : status === 'BLOCKED' || status === 'STUCK'
                    ? 'Blocked'
                    : status === 'NOT_STARTED'
                      ? 'Not Started'
                      : status === 'CANCELLED'
                        ? 'Cancelled'
                        : status === 'PAUSED'
                          ? 'Paused'
                          : status;
        toast.success(`Successfully set status to ${statusText} for ${updatedCount} task${updatedCount > 1 ? 's' : ''}`);
    };

    const applyBulkStatusPatch = async (
        taskIds,
        newStatus,
        { cascadeStatus = false, updateOnly, resumeDownstream = false, recalculateDownstream = false, adjustDownstreamDeadlines = false } = {},
    ) => {
        const updates = { status: newStatus };
        if (isCompletedStatus(newStatus)) {
            updates.starred = false;
        }
        if (cascadeStatus) updates.cascade_status = true;
        if (resumeDownstream) updates.resume_downstream = true;
        if (updateOnly !== undefined) updates.update_only = updateOnly;
        if (recalculateDownstream) updates.recalculate_downstream = true;
        if (adjustDownstreamDeadlines) updates.adjust_downstream_deadlines = true;
        await dispatch(
            BulkUpdateTasks({
                taskIds,
                updates,
                suppressToast: true,
            }),
        );
        handleBulkStatusUpdate(newStatus, taskIds.length);
    };

    const handleBulkStatusSelect = async (status, taskIds) => {
        const normalized = normalizeTaskStatus(status);
        const selectedTasks = (taskData || []).filter(t => taskIds.includes(t.id));
        const anyPausedOrCancelled = selectedTasks.some(t => ['PAUSED', 'CANCELLED'].includes(normalizeTaskStatus(t.status)));

        if (['PAUSED', 'CANCELLED'].includes(normalized)) {
            setPendingBulkStatusChange({
                taskIds,
                newStatus: normalized,
                action: normalized === 'CANCELLED' ? 'cancel' : 'pause',
            });
            return;
        }

        if (anyPausedOrCancelled && !['PAUSED', 'CANCELLED'].includes(normalized)) {
            setPendingBulkStatusChange({
                taskIds,
                newStatus: normalized,
                action: 'resume',
            });
            return;
        }

        if (['WORKING_ON_IT', 'COMPLETED', 'COMPLETE'].includes(normalized)) {
            await requestBulkStatusScheduleMutation({
                taskIds,
                newStatus: normalized,
                setPending: setPendingScheduleChange,
                applyBulkPatch: applyBulkStatusPatch,
                setPreviewTaskId,
            });
            return;
        }

        await applyBulkStatusPatch(taskIds, normalized, { updateOnly: true });
    };

    const handleBulkStatusThisOnly = async () => {
        if (!pendingBulkStatusChange || bulkStatusChangeLoading) return;
        setBulkStatusChangeLoading(true);
        try {
            const { taskIds, newStatus, action } = pendingBulkStatusChange;
            await applyBulkStatusPatch(taskIds, newStatus, {
                cascadeStatus: false,
                updateOnly: action === 'resume',
            });
            setPendingBulkStatusChange(null);
        } catch (error) {
            console.error('Error updating bulk task status:', error);
        } finally {
            setBulkStatusChangeLoading(false);
        }
    };

    const handleBulkStatusWithFollowing = async () => {
        if (!pendingBulkStatusChange || bulkStatusChangeLoading) return;
        setBulkStatusChangeLoading(true);
        try {
            const { taskIds, newStatus, action } = pendingBulkStatusChange;
            if (action === 'resume') {
                await applyBulkStatusPatch(taskIds, newStatus, {
                    cascadeStatus: true,
                    resumeDownstream: true,
                    recalculateDownstream: true,
                });
            } else {
                await applyBulkStatusPatch(taskIds, newStatus, { cascadeStatus: true });
            }
            setPendingBulkStatusChange(null);
        } catch (error) {
            console.error('Error updating bulk task status:', error);
        } finally {
            setBulkStatusChangeLoading(false);
        }
    };

    const handleBulkDeadlineUpdate = (deadlineData, updatedCount) => {
        // Handle bulk deadline update completion
        if (deadlineData.deadline) {
            toast.success(`Successfully set deadline for ${updatedCount} task${updatedCount > 1 ? 's' : ''}`);
        } else {
            toast.success(`Successfully cleared deadline for ${updatedCount} task${updatedCount > 1 ? 's' : ''}`);
        }
        // Refresh the project data to show updated deadlines
    };

    const applyBulkDeadlinePatch = async (taskIds, deadline, { updateOnly, adjustDownstreamDeadlines } = {}) => {
        const updates = { deadline: deadline || null };
        if (updateOnly !== undefined) updates.update_only = updateOnly;
        if (adjustDownstreamDeadlines) updates.adjust_downstream_deadlines = true;
        await dispatch(
            BulkUpdateTasks({
                taskIds,
                updates,
                suppressToast: true,
            }),
        );
        handleBulkDeadlineUpdate({ deadline }, taskIds.length);
    };

    const handleBulkDeadlineRequest = async (taskIds, deadline) => {
        await requestBulkDeadlineScheduleMutation({
            taskIds,
            deadline,
            setPending: setPendingScheduleChange,
            applyBulkPatch: applyBulkDeadlinePatch,
            setPreviewTaskId,
        });
    };

    const handleDownloadTemplate = () => {
        dispatch(DownloadCSVTemplate());
    };

    const handleImportCSV = () => {
        document.getElementById('csv-upload-task-management').click();
    };

    const handleExportCSV = () => {
        if (!exportCSVLoading) {
            dispatch(ExportTasksCSV());
        }
    };

    const handleExportSelectedCSV = () => {
        if (exportCSVLoading || selectedRows.length === 0) {
            return;
        }
        dispatch(ExportTasksCSV(selectedRows));
    };

    const handleExportPDF = async () => {
        if (exportPDFLoading) {
            return;
        }

        setExportPDFLoading(true);
        try {
            await exportTasksToPdf({
                filteredData,
                projectName: selectedProject?.name,
                selectedProject,
                projectUsersAll,
                allTaskData: taskData,
                isColumnVisible,
                columnOrder: readTaskTableColumnOrder(),
                customOrder,
                filename: 'tasks.pdf',
            });
            toast.success('Tasks exported to PDF');
        } catch (error) {
            toast.error(error?.message || 'Failed to export tasks to PDF');
        } finally {
            setExportPDFLoading(false);
        }
    };

    const handleExportSelectedPDF = async () => {
        if (exportPDFLoading || selectedRows.length === 0) {
            return;
        }

        setExportPDFLoading(true);
        try {
            await exportTasksToPdf({
                filteredData,
                projectName: selectedProject?.name,
                selectedProject,
                projectUsersAll,
                allTaskData: taskData,
                isColumnVisible,
                columnOrder: readTaskTableColumnOrder(),
                customOrder,
                selectedTaskIds: selectedRows,
                filename: `tasks_selected_${selectedRows.length}.pdf`,
            });
            toast.success(`Successfully exported ${selectedRows.length} task${selectedRows.length > 1 ? 's' : ''} to PDF`);
        } catch (error) {
            toast.error(error?.message || 'Failed to export tasks to PDF');
        } finally {
            setExportPDFLoading(false);
        }
    };

    const handleFileUpload = event => {
        const file = event.target.files[0];
        if (!file) {
            return;
        }

        const fileName = file.name?.toLowerCase() || '';

        // For non-CSV files (e.g. .xlsx), keep existing behaviour and
        // send directly to the backend without column mapping.
        if (!fileName.endsWith('.csv')) {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('default_status', 'NOT_STARTED');
            dispatch(UploadCSVFile(formData));
            return;
        }

        // For CSV files, read the first non-empty line client‑side and treat it as the header row.
        // This avoids bundling papaparse (and its Node stream dependency) while still supporting header mapping.
        const reader = new FileReader();
        reader.onload = e => {
            try {
                const text = (e.target?.result || '').toString();
                const firstNonEmptyLine = text.split(/\r?\n/).find(line => line.trim().length > 0) || '';

                if (!firstNonEmptyLine) {
                    toast.error('No headers found in CSV file.');
                    return;
                }

                const headers = parseCsvHeader(firstNonEmptyLine);
                if (!headers.length) {
                    toast.error('No headers found in CSV file.');
                    return;
                }

                const normalizedHeaders = headers.map(h => (h || '').toString().trim());

                const fieldAliases = {
                    task: ['task', 'task name', 'title', 'item', 'description'],
                    tag_repr: ['tag', 'tags', 'category', 'categories'],
                    workstream_repr: ['workstream', 'stream', 'work stream'],
                    deadline: ['deadline', 'due date', 'due', 'target date', 'end date', 'end_date', 'end', 'enddate'],
                    project_phase: ['project_phase', 'project phase', 'phase'],
                    task_id: ['task_id', 'task id', 'row id', 'rowid', 'row'],
                    parent_task_id: ['parent_task_id', 'parent task id', 'parent id', 'parent row id'],
                };

                const guessedMapping = {
                    task: '',
                    tag_repr: '',
                    workstream_repr: '',
                    deadline: '',
                    project_phase: '',
                    task_id: '',
                    parent_task_id: '',
                };

                const findBestMatch = aliases => {
                    const loweredAliases = aliases.map(a => a.toLowerCase());
                    // Exact / trimmed match first
                    for (const header of normalizedHeaders) {
                        const h = header.toLowerCase();
                        if (loweredAliases.includes(h)) {
                            return header;
                        }
                    }
                    // Then "contains" match (e.g. "Task Name (Text)")
                    for (const header of normalizedHeaders) {
                        const h = header.toLowerCase();
                        if (loweredAliases.some(a => h.includes(a))) {
                            return header;
                        }
                    }
                    return '';
                };

                Object.keys(fieldAliases).forEach(fieldKey => {
                    const match = findBestMatch(fieldAliases[fieldKey]);
                    if (match) {
                        guessedMapping[fieldKey] = match;
                    }
                });

                setCsvFileForMapping(file);
                setCsvHeaders(headers);
                setCsvMapping(prev => ({
                    ...prev,
                    ...guessedMapping,
                    // Ensure Task is always mapped to something if we can.
                    task: guessedMapping.task || prev.task || headers[0],
                }));
            } catch (err) {
                console.error('Failed to read CSV file:', err);
                toast.error('Failed to read CSV file. Please check the file format.');
            }
        };
        reader.onerror = () => {
            toast.error('Failed to read CSV file. Please check the file format.');
        };
        reader.readAsText(file);
    };

    const handleChangeCsvMapping = (fieldKey, headerName) => {
        setCsvMapping(prev => ({
            ...prev,
            [fieldKey]: headerName,
        }));
    };

    const handleConfirmCsvImport = () => {
        if (!csvFileForMapping) {
            return;
        }

        // Require at least Task to be mapped so import is meaningful.
        if (!csvMapping.task) {
            toast.error('Please map at least the Task column before importing.');
            return;
        }

        const formData = new FormData();
        formData.append('file', csvFileForMapping);
        formData.append('column_mapping', JSON.stringify(csvMapping));
        formData.append('default_status', 'NOT_STARTED');
        dispatch(UploadCSVFile(formData));

        // Reset mapping state after dispatch
        setCsvFileForMapping(null);
        setCsvHeaders([]);
        setCsvMapping({
            task: '',
            tag_repr: '',
            workstream_repr: '',
            deadline: '',
            project_phase: '',
            task_id: '',
            parent_task_id: '',
        });
    };

    const handleCancelCsvImport = () => {
        setCsvFileForMapping(null);
        setCsvHeaders([]);
        setCsvMapping({
            task: '',
            tag_repr: '',
            workstream_repr: '',
            deadline: '',
            project_phase: '',
            task_id: '',
            parent_task_id: '',
        });
    };

    const handleImportTechDDTasks = () => {
        dispatch(ImportTechDDTasks());
    };

    const handleToggleTable = () => {
        setTableCollapsed(!tableCollapsed);
    };

    const handleAddNewTask = () => {
        // Collapse any expanded tasks
        setExpandedTasks(new Set());
        // Focus the bottom add row and prepare input
        setIsAddingNewTask(true);
        setNewTaskText('');
        // Bump focus trigger so TasksTable effect runs even if isAddingNewTask was already true
        setAddNewTaskFocusTrigger(prev => prev + 1);
    };

    const handleAddFilter = () => {
        setFilters(prev => [...prev, { column: '', value: '', id: Date.now() }]);
        setSelectedRows([]); // Clear selected rows when filters are added
    };

    const handleRemoveFilter = filterId => {
        setFilters(prev => prev.filter(filter => filter.id !== filterId));
        setSelectedRows([]); // Clear selected rows when filters are removed
    };

    const handleUpdateFilter = (filterId, field, value) => {
        setFilters(prev => prev.map(filter => (filter.id === filterId ? { ...filter, [field]: value } : filter)));
        setSelectedRows([]); // Clear selected rows when filters change
    };

    const handleClearAllFilters = () => {
        setFilters([{ column: '', value: '', id: Date.now() }]);
        setSelectedRows([]); // Clear selected rows when filters are cleared
        setCustomOrder(null); // Clear custom order when filters are cleared
    };

    const handleClearSort = () => {
        setSortColumn('');
        setSortOrder('');
        setCustomOrder(null); // Clear custom order when sorting is cleared
    };

    const handleClearGroupBy = () => {
        dispatch(clearGroupBySettings());
        // Persist the cleared choice so reload does not restore the default grouping
        if (selectedProject?.id) {
            saveGroupBySettings(selectedProject.id, { column: '', sort: '', collapsedGroups: [] });
        }
        setCustomOrder(null); // Clear custom order when grouping is cleared
    };

    const handleToggleGroup = groupName => {
        const newCollapsedGroups = new Set(collapsedGroups);
        if (newCollapsedGroups.has(groupName)) {
            newCollapsedGroups.delete(groupName);
        } else {
            newCollapsedGroups.add(groupName);
        }
        dispatch(setCollapsedGroups(Array.from(newCollapsedGroups)));
    };

    const commitDropIntent = React.useCallback(nextIntent => {
        const prev = dropIntentRef.current;
        const prevTargetId = prev?.targetId ?? null;
        const nextTargetId = nextIntent?.targetId ?? null;

        if (prevTargetId === nextTargetId && prev?.label === nextIntent?.label && prev?.valid === nextIntent?.valid && prev?.reason === nextIntent?.reason) {
            return;
        }

        dropIntentRef.current = nextIntent;
        setDropIntent(nextIntent);
    }, []);

    const resolveIntentForEvent = React.useCallback(
        (active, over, deltaX = 0) => {
            const draggedTask = taskMapRef.current.get(String(active.id));
            const targetTask = taskMapRef.current.get(String(over.id));
            if (!draggedTask || !targetTask) return null;

            const metrics = getDropIntentMetrics(dragPointerRef.current, over.rect, deltaX);
            const intent = resolveDropIntent(draggedTask, targetTask, taskData, metrics);
            return { draggedTask, targetTask, intent };
        },
        [taskData],
    );

    const updateDropIntent = React.useCallback(
        (event, deltaX = 0) => {
            const { active, over } = event;
            if (!over || active.id === over.id) {
                commitDropIntent(null);
                return;
            }

            const resolved = resolveIntentForEvent(active, over, deltaX);
            if (!resolved) {
                commitDropIntent(null);
                return;
            }

            const { intent } = resolved;

            if (!intent.valid) {
                commitDropIntent({ targetId: String(over.id), label: 'invalid', ...intent });
                return;
            }

            commitDropIntent({ targetId: String(over.id), ...intent });
        },
        [commitDropIntent, resolveIntentForEvent],
    );

    const handleDragStart = event => {
        setActiveDragId(String(event.active.id));
        const activatorEvent = event.activatorEvent;
        if (activatorEvent && 'clientX' in activatorEvent) {
            dragPointerRef.current = {
                startX: activatorEvent.clientX,
                startY: activatorEvent.clientY,
                x: activatorEvent.clientX,
                y: activatorEvent.clientY,
            };
        }
    };

    const handleDragMove = event => {
        dragPointerRef.current = {
            ...dragPointerRef.current,
            x: dragPointerRef.current.startX + event.delta.x,
            y: dragPointerRef.current.startY + event.delta.y,
        };

        if (dragIntentFrameRef.current != null) {
            return;
        }

        dragIntentFrameRef.current = requestAnimationFrame(() => {
            dragIntentFrameRef.current = null;
            updateDropIntent(event, event.delta.x);
        });
    };

    const clearDragState = React.useCallback(() => {
        if (dragIntentFrameRef.current != null) {
            cancelAnimationFrame(dragIntentFrameRef.current);
            dragIntentFrameRef.current = null;
        }
        dropIntentRef.current = null;
        setDropIntent(null);
        setActiveDragId(null);
    }, []);

    const handleDragCancel = () => {
        clearDragState();
    };

    const applyHierarchyMove = async ({ draggedTask, targetTask, intent }) => {
        const { tasksWithUpdatedParent, orderedSiblings, nextCustomOrder } = applyTaskMove(taskData, draggedTask, targetTask, intent, customOrder);
        const oldParentId = normalizeParentId(draggedTask.parent_task);
        const parentChanged = oldParentId !== normalizeParentId(intent.parentId);

        if (parentChanged) {
            await dispatch(
                PatchTask({
                    id: draggedTask.id,
                    body: { parent_task: intent.parentId },
                    suppressToast: true,
                }),
            );
        }

        if (intent.label === 'nest') {
            setExpandedTasks(prev => new Set([...prev, targetTask.id, ...getAncestorIds(targetTask, taskData)]));
        }

        setCustomOrder(nextCustomOrder);

        const taskOrders = buildTaskReorderPayload(tasksWithUpdatedParent, draggedTask, orderedSiblings, oldParentId, intent.parentId);

        if (!taskOrders.length) {
            return;
        }

        await requestReorderScheduleMutation({
            taskOrders,
            setPending: pending =>
                setPendingScheduleChange({
                    ...pending,
                    taskOrders,
                }),
            applyReorder: async (orders, { recalculateDownstream }) => {
                await dispatch(
                    ReorderTasks({
                        taskOrders: orders,
                        recalculateDownstream,
                    }),
                ).unwrap();
                setCustomOrder(null);
            },
            setPreviewTaskId,
        });

        if (intent.label === 'nest') {
            toast.success('Task moved into hierarchy');
        } else if (intent.label === 'promote') {
            toast.success(intent.parentId == null ? 'Task moved to top level' : 'Task moved up one level');
        } else {
            toast.success('Task order updated');
        }
    };

    const handleHierarchyMoveCancel = () => {
        if (hierarchyMoveLoading) return;
        setPendingHierarchyMove(null);
    };

    const handleHierarchyMoveContinue = async () => {
        if (!pendingHierarchyMove || hierarchyMoveLoading) return;

        setHierarchyMoveLoading(true);
        try {
            await applyHierarchyMove(pendingHierarchyMove);
            setPendingHierarchyMove(null);
        } catch (error) {
            console.error('Failed to update task hierarchy:', error);
            toast.error('Failed to update task');
        } finally {
            setHierarchyMoveLoading(false);
        }
    };

    const handleDragEnd = async event => {
        const { active, over, delta } = event;

        if (dragIntentFrameRef.current != null) {
            cancelAnimationFrame(dragIntentFrameRef.current);
            dragIntentFrameRef.current = null;
        }
        dropIntentRef.current = null;
        setDropIntent(null);
        setActiveDragId(null);

        if (!over || active.id === over.id) {
            return;
        }

        const resolved = resolveIntentForEvent(active, over, delta.x);
        if (!resolved) {
            return;
        }

        const { draggedTask, targetTask, intent } = resolved;

        if (!intent.valid) {
            if (intent.reason === 'circular') {
                toast.error('Cannot move a task into its own subtask');
            } else if (intent.reason === 'maxDepth') {
                toast.error('Tasks can only be nested up to three levels deep');
            }
            return;
        }

        const parentChanged = normalizeParentId(draggedTask.parent_task) !== normalizeParentId(intent.parentId);
        if (parentChanged) {
            setPendingHierarchyMove({ draggedTask, targetTask, intent });
            return;
        }

        try {
            await applyHierarchyMove({ draggedTask, targetTask, intent });
        } catch (error) {
            console.error('Failed to update task hierarchy:', error);
            toast.error('Failed to update task');
        }
    };

    const handleOpenSidebar = (task, focus = null) => {
        setSelectedTaskForSidebar(task);
        setSidebarOpen(true);
        setSidebarFocusRequest(focus ? { type: focus, nonce: Date.now() } : null);
    };

    const handleCloseSidebar = () => {
        setSidebarOpen(false);
        setSelectedTaskForSidebar(null);
        setSidebarFocusRequest(null);
    };

    const applySchedulePatch = async (
        task,
        payload,
        { updateOnly, adjustDownstreamDeadlines, adjustTimelineToDeadline } = {},
    ) => {
        const body = { ...payload, update_only: updateOnly };
        if (adjustDownstreamDeadlines) body.adjust_downstream_deadlines = true;
        if (adjustTimelineToDeadline) body.adjust_timeline_to_deadline = true;
        await dispatch(
            PatchTask({
                body,
                id: task.id,
                suppressToast: true,
            }),
        ).unwrap();
    };

    const handleRequestTimelineChange = (task, payload) => {
        requestScheduleMutation({
            task,
            payload,
            fieldLabel: 'Timeline',
            defaultUpdateOnly: true,
            setPending: setPendingScheduleChange,
            applyPatch: applySchedulePatch,
            setPreviewTaskId,
        }).catch(error => console.error('Error updating timeline:', error));
    };

    const handleRequestDurationChange = (task, duration) => {
        requestScheduleMutation({
            task,
            payload: { duration },
            fieldLabel: 'Duration',
            defaultUpdateOnly: true,
            setPending: setPendingScheduleChange,
            applyPatch: applySchedulePatch,
            setPreviewTaskId,
        }).catch(error => console.error('Error updating duration:', error));
    };

    const handleRequestModeChange = (task, mode) => {
        requestScheduleMutation({
            task,
            payload: { mode },
            fieldLabel: 'Mode',
            defaultUpdateOnly: true,
            setPending: setPendingScheduleChange,
            applyPatch: applySchedulePatch,
            setPreviewTaskId,
        }).catch(error => console.error('Error updating mode:', error));
    };

    const handleRequestDeadlineChange = (task, { deadline }) => {
        requestDeadlineMutation({
            task,
            deadline,
            setPendingDeadline: setPendingDeadlineChange,
            setPendingSchedule: setPendingScheduleChange,
            applyPatch: applySchedulePatch,
            setPreviewTaskId,
        }).catch(error => console.error('Error updating deadline:', error));
    };

    const applyDeadlineChange = async ({ adjustTimelineToDeadline = false } = {}) => {
        if (!pendingDeadlineChange || deadlineChangeLoading) return;
        setDeadlineChangeLoading(true);
        try {
            const { task, payload } = pendingDeadlineChange;
            if (adjustTimelineToDeadline) {
                const { fetchSchedulePreview } = await import('../../utils/taskSchedulingUtils');
                const preview = await fetchSchedulePreview(task.id, payload, { adjustTimelineToDeadline: true });
                if (preview.would_affect_others) {
                    setPendingDeadlineChange(null);
                    setPendingScheduleChange({
                        task,
                        payload,
                        fieldLabel: 'Timeline',
                        affectedCount: preview.affected_count,
                        previewFlags: { adjustTimelineToDeadline: true },
                    });
                    return;
                }
                await applySchedulePatch(task, payload, {
                    updateOnly: true,
                    adjustTimelineToDeadline: true,
                });
            } else {
                await applySchedulePatch(task, payload, { updateOnly: true });
            }
            setPendingDeadlineChange(null);
        } catch (error) {
            console.error('Error updating deadline:', error);
        } finally {
            setDeadlineChangeLoading(false);
        }
    };

    const applyScheduleChange = async ({ updateOnly, adjustDownstreamDeadlines = false } = {}) => {
        if (!pendingScheduleChange || scheduleChangeLoading) return;
        setScheduleChangeLoading(true);
        try {
            if (pendingScheduleChange.taskOrders) {
                await dispatch(
                    ReorderTasks({
                        taskOrders: pendingScheduleChange.taskOrders,
                        recalculateDownstream: !updateOnly,
                    }),
                ).unwrap();
                setCustomOrder(null);
            } else if (pendingScheduleChange.bulkUpdate) {
                await applyBulkStatusPatch(pendingScheduleChange.taskIds, pendingScheduleChange.payload.status, {
                    recalculateDownstream: !updateOnly,
                    adjustDownstreamDeadlines,
                });
            } else if (pendingScheduleChange.bulkDeadlineUpdate) {
                await applyBulkDeadlinePatch(pendingScheduleChange.taskIds, pendingScheduleChange.payload.deadline, {
                    updateOnly,
                    adjustDownstreamDeadlines,
                });
            } else {
                await applySchedulePatch(pendingScheduleChange.task, pendingScheduleChange.payload, {
                    updateOnly,
                    adjustDownstreamDeadlines,
                    adjustTimelineToDeadline: pendingScheduleChange.previewFlags?.adjustTimelineToDeadline,
                });
            }
            setPendingScheduleChange(null);
        } catch (error) {
            showScheduleError(error);
            console.error('Error updating schedule:', error);
        } finally {
            setScheduleChangeLoading(false);
        }
    };

    const applyStatusPatch = async (task, newStatus, { cascadeStatus = false, updateOnly, resumeDownstream = false } = {}) => {
        const body = statusPatchBody(task, newStatus);
        if (cascadeStatus) body.cascade_status = true;
        if (resumeDownstream) body.resume_downstream = true;
        if (updateOnly !== undefined) body.update_only = updateOnly;
        await dispatch(PatchTask({ body, id: task.id, suppressToast: true }));
    };

    const handleRequestStatusChange = async (task, newStatus) => {
        const normalized = normalizeTaskStatus(newStatus);
        const prev = normalizeTaskStatus(task?.status);
        const pauseCancelResume = shouldConfirmPauseCancelResume(normalized, prev);
        if (pauseCancelResume) {
            setPendingStatusChange({
                task,
                newStatus: normalized,
                action: pauseCancelResume.action,
            });
            return;
        }
        if (statusNeedsSchedulePreview(normalized, prev)) {
            try {
                await requestStatusScheduleMutation({
                    task,
                    newStatus: normalized,
                    setPending: setPendingScheduleChange,
                    applyPatch: applySchedulePatch,
                    setPreviewTaskId,
                });
            } catch (error) {
                console.error('Error updating task status:', error);
            }
            return;
        }
        applyStatusPatch(task, normalized, { updateOnly: true });
    };

    const handleStatusThisOnly = async () => {
        if (!pendingStatusChange || statusChangeLoading) return;
        setStatusChangeLoading(true);
        try {
            const { task, newStatus, action } = pendingStatusChange;
            await applyStatusPatch(task, newStatus, {
                cascadeStatus: false,
                updateOnly: action === 'resume',
            });
            setPendingStatusChange(null);
        } catch (error) {
            console.error('Error updating task status:', error);
        } finally {
            setStatusChangeLoading(false);
        }
    };

    const handleStatusWithFollowing = async () => {
        if (!pendingStatusChange || statusChangeLoading) return;
        setStatusChangeLoading(true);
        try {
            const { task, newStatus, action } = pendingStatusChange;
            if (action === 'resume') {
                await applyStatusPatch(task, newStatus, {
                    cascadeStatus: true,
                    resumeDownstream: true,
                    updateOnly: false,
                });
            } else {
                await applyStatusPatch(task, newStatus, { cascadeStatus: true });
            }
            setPendingStatusChange(null);
        } catch (error) {
            console.error('Error updating task status:', error);
        } finally {
            setStatusChangeLoading(false);
        }
    };

    const getSortColumnOptions = () => [
        { value: 'owner', label: 'Owner' },
        { value: 'status', label: 'Status' },
        { value: 'priority', label: 'Priority' },
        { value: 'timeline', label: 'Timeline' },
        { value: 'duration', label: 'Duration' },
        { value: 'deadline', label: 'Deadline' },
        { value: 'project_phase', label: 'Project Phase' },
        { value: 'tag', label: 'Tag' },
        { value: 'workstream', label: 'Workstream' },
        { value: 'lastUpdated', label: 'Last Updated' },
        { value: 'addedOn', label: 'Added On' },
    ];

    const getSortOrderOptions = () => [
        { value: 'asc', label: 'Ascending' },
        { value: 'desc', label: 'Descending' },
    ];

    const getColumnVisibilityOptions = () => {
        const options = [
        { value: 'showAll', label: 'Show all' },
        { value: 'owner', label: 'Owner' },
        { value: 'status', label: 'Status' },
        { value: 'timeline', label: 'Timeline' },
        { value: 'duration', label: 'Duration' },
        ...(isSchedulingMode(selectedProject) ? [{ value: 'mode', label: 'Mode' }] : []),
        { value: 'deadline', label: 'Deadline' },
        { value: 'priority', label: 'Priority' },
        { value: 'workstream', label: 'Workstream' },
        { value: 'tag', label: 'Tag' },
        { value: 'projectPhase', label: 'Project Phase' },
        { value: 'lastUpdated', label: 'Last Updated' },
    ];
        return options;
    };

    const handleShowAllColumns = checked => {
        if (checked) {
            setShowAllColumns(true);
            setHiddenColumns([]);
        } else {
            setShowAllColumns(false);
        }
    };

    const handleColumnVisibility = (column, checked) => {
        if (column === 'showAll') {
            handleShowAllColumns(checked);
            return;
        }

        if (checked) {
            // Show column
            setHiddenColumns(prev => prev.filter(col => col !== column));
            if (hiddenColumns.length === 0) {
                setShowAllColumns(true);
            }
        } else {
            // Hide column
            setHiddenColumns(prev => [...prev, column]);
            setShowAllColumns(false);
        }
    };

    const isColumnVisible = column => {
        if (column === 'mode' && !isSchedulingMode(selectedProject)) return false;
        if (showAllColumns) return true;
        return !hiddenColumns.includes(column);
    };

    const getUserInfo = createdBy => {
        const user = projectUsersAll?.find(user => user?.value?.user_id === createdBy);
        return user?.value || null;
    };

    const getDurationMinutes = task => {
        if (task?.duration == null || task.duration === '') return null;
        const minutes = Number(task.duration);
        return Number.isNaN(minutes) ? null : minutes;
    };

    const formatDurationFilterLabel = minutes => formatDuration(minutes) || '-- --';

    const applyFilters = data => {
        if (!data || filters.length === 0) return data;

        return data.filter(task => {
            return filters.every(filter => {
                const hasValue = Array.isArray(filter.value) ? filter.value.length > 0 : !!filter.value;
                if (!filter.column || !hasValue) return true; // Skip empty filters

                switch (filter.column) {
                    case 'owner':
                        const userInfo = getUserInfo(task.assigned_to || task.created_by);
                        return userInfo?.user_id === filter.value;
                    case 'status':
                        return task.status === filter.value;
                    case 'priority':
                        return task.priority === filter.value;
                    case 'tag': {
                        // Multi-tag filter: keep tasks that have at least one of the selected tags.
                        const selectedTags = Array.isArray(filter.value) ? filter.value : [filter.value];
                        if (selectedTags.length === 0) return true;
                        const taskTags = Array.isArray(task.tags) ? task.tags : task?.tag_repr ? [task.tag_repr] : [];
                        return taskTags.some(tag => selectedTags.includes(tag));
                    }
                    case 'workstream':
                        return task.workstream_repr === filter.value;
                    case 'project_phase':
                        return (task.project_phase || '') === filter.value;
                    case 'duration': {
                        const minutes = getDurationMinutes(task);
                        if (minutes == null) return false;
                        return formatDurationFilterLabel(minutes) === filter.value;
                    }
                    case 'mode':
                        return (task.mode || 'FS') === filter.value;
                    default:
                        return true;
                }
            });
        });
    };

    const applySorting = data => {
        if (!sortColumn || !sortOrder) return data;

        return [...data].sort((a, b) => {
            let aValue, bValue;

            switch (sortColumn) {
                case 'timeline': {
                    const aTimeline = getInheritedTimeline(a, taskData || []);
                    const bTimeline = getInheritedTimeline(b, taskData || []);
                    const aTime = aTimeline?.start_date ? new Date(aTimeline.start_date).getTime() : null;
                    const bTime = bTimeline?.start_date ? new Date(bTimeline.start_date).getTime() : null;
                    const aNull = aTime === null || Number.isNaN(aTime);
                    const bNull = bTime === null || Number.isNaN(bTime);
                    if (aNull && bNull) return 0;
                    if (aNull) return 1; // nulls last
                    if (bNull) return -1;
                    return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
                }
                case 'deadline': {
                    const aTime = a?.deadline ? new Date(a.deadline).getTime() : null;
                    const bTime = b?.deadline ? new Date(b.deadline).getTime() : null;
                    const aNull = aTime === null || Number.isNaN(aTime);
                    const bNull = bTime === null || Number.isNaN(bTime);
                    if (aNull && bNull) return 0;
                    if (aNull) return 1; // nulls last
                    if (bNull) return -1;
                    return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
                }
                case 'duration': {
                    const aMinutes = getDurationMinutes(a);
                    const bMinutes = getDurationMinutes(b);
                    const aNull = aMinutes == null;
                    const bNull = bMinutes == null;
                    if (aNull && bNull) return 0;
                    if (aNull) return 1; // nulls last
                    if (bNull) return -1;
                    return sortOrder === 'asc' ? aMinutes - bMinutes : bMinutes - aMinutes;
                }
                case 'project_phase':
                    aValue = (a.project_phase || '').toString().toLowerCase();
                    bValue = (b.project_phase || '').toString().toLowerCase();
                    break;
                case 'lastUpdated': {
                    const aTime = a?.last_updated ? new Date(a.last_updated).getTime() : null;
                    const bTime = b?.last_updated ? new Date(b.last_updated).getTime() : null;
                    const aNull = aTime === null || Number.isNaN(aTime);
                    const bNull = bTime === null || Number.isNaN(bTime);
                    if (aNull && bNull) return 0;
                    if (aNull) return 1; // nulls last
                    if (bNull) return -1;
                    return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
                }
                case 'addedOn': {
                    const aTime = a?.created ? new Date(a.created).getTime() : null;
                    const bTime = b?.created ? new Date(b.created).getTime() : null;
                    const aNull = aTime === null || Number.isNaN(aTime);
                    const bNull = bTime === null || Number.isNaN(bTime);
                    if (aNull && bNull) return 0;
                    if (aNull) return 1; // nulls last
                    if (bNull) return -1;
                    return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
                }
                case 'owner':
                    const userInfoA = getUserInfo(a.assigned_to || a.created_by);
                    const userInfoB = getUserInfo(b.assigned_to || b.created_by);
                    aValue = userInfoA?.full_name || '';
                    bValue = userInfoB?.full_name || '';
                    break;
                case 'status':
                    aValue = a.status || '';
                    bValue = b.status || '';
                    break;
                case 'priority':
                    aValue = a.priority || '';
                    bValue = b.priority || '';
                    break;
                case 'tag': {
                    const aTags = Array.isArray(a.tags) ? a.tags : a?.tag_repr ? [a.tag_repr] : [];
                    const bTags = Array.isArray(b.tags) ? b.tags : b?.tag_repr ? [b.tag_repr] : [];
                    aValue = aTags[0] || '';
                    bValue = bTags[0] || '';
                    break;
                }
                case 'workstream':
                    aValue = a.workstream_repr || '';
                    bValue = b.workstream_repr || '';
                    break;
                default:
                    return 0;
            }

            if (sortOrder === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    };

    // Helper function to get display label for status and priority
    const getDisplayLabel = (value, type) => {
        if (!value) return type === 'status' ? 'Not Started' : 'No Priority';

        if (type === 'status') {
            const statusMap = {
                NOT_STARTED: 'Not Started',
                WORKING_ON_IT: 'Working on it',
                COMPLETED: 'Completed',
                STUCK: 'Blocked',
                CANCELLED: 'Cancelled',
                BLOCKED: 'Blocked',
                PAUSED: 'Paused',
                PENDING: 'Working on it',
                COMPLETE: 'Completed',
                NOT_APPLICABLE: 'Not Started',
                'Not Applicable': 'Not Started',
            };
            return statusMap[value] || value;
        }

        if (type === 'priority') {
            const priorityMap = {
                HIGH: 'High',
                MEDIUM: 'Medium',
                LOW: 'Low',
            };
            return priorityMap[value] || value;
        }

        return value;
    };

    const applyGrouping = data => {
        if (!groupByColumn || !data) return { isGrouped: false, data };

        // Group tasks by the selected field
        const grouped = {};

        data.forEach(task => {
            let groupKey = '';

            switch (groupByColumn) {
                case 'owner':
                    const userInfo = getUserInfo(task.assigned_to || task.created_by);
                    groupKey = userInfo?.full_name || 'Unassigned';
                    break;
                case 'status':
                    groupKey = getDisplayLabel(task.status, 'status');
                    break;
                case 'priority':
                    groupKey = getDisplayLabel(task.priority, 'priority');
                    break;
                case 'deadline': {
                    if (!task.deadline) {
                        groupKey = 'No Deadline';
                    } else {
                        try {
                            const d = new Date(task.deadline);
                            if (Number.isNaN(d.getTime())) {
                                groupKey = 'No Deadline';
                            } else {
                                groupKey = d.toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: '2-digit',
                                });
                            }
                        } catch {
                            groupKey = 'No Deadline';
                        }
                    }
                    break;
                }
                case 'workstream':
                    groupKey = task.workstream_repr || 'No Workstream';
                    break;
                case 'project_phase':
                    groupKey = task.project_phase || 'No Project Phase';
                    break;
                case 'duration': {
                    const minutes = getDurationMinutes(task);
                    groupKey = minutes == null ? 'No Duration' : formatDurationFilterLabel(minutes);
                    break;
                }
                default:
                    groupKey = 'Other';
            }

            if (!grouped[groupKey]) {
                grouped[groupKey] = [];
            }
            grouped[groupKey].push(task);
        });

        // Sort groups if groupBySort is specified
        const sortedGroups = Object.entries(grouped).sort(([a], [b]) => {
            if (groupBySort === 'asc') {
                return a.localeCompare(b);
            } else if (groupBySort === 'desc') {
                return b.localeCompare(a);
            }
            return 0;
        });

        // Return grouped data structure
        return {
            isGrouped: true,
            groups: sortedGroups.map(([groupName, tasks]) => ({
                groupName,
                tasks,
                taskCount: tasks.length,
            })),
        };
    };

    const getColumnOptions = () => [
        { value: 'owner', label: 'Owner' },
        { value: 'status', label: 'Status' },
        { value: 'priority', label: 'Priority' },
        { value: 'mode', label: 'Mode' },
        { value: 'tag', label: 'Tag' },
        { value: 'workstream', label: 'Workstream' },
        { value: 'project_phase', label: 'Project Phase' },
        { value: 'duration', label: 'Duration' },
    ];

    const getValueOptions = column => {
        switch (column) {
            case 'owner':
                return (
                    projectUsersAll?.map(user => ({
                        value: user.value?.user_id,
                        label: user.value?.full_name,
                    })) || []
                );
            case 'status':
                return [
                    { value: 'NOT_STARTED', label: 'Not Started' },
                    { value: 'WORKING_ON_IT', label: 'Working on it' },
                    { value: 'COMPLETED', label: 'Completed' },
                    { value: 'BLOCKED', label: 'Blocked' },
                    { value: 'PAUSED', label: 'Paused' },
                    { value: 'CANCELLED', label: 'Cancelled' },
                ];
            case 'priority':
                return [
                    { value: 'HIGH', label: 'High' },
                    { value: 'MEDIUM', label: 'Medium' },
                    { value: 'LOW', label: 'Low' },
                ];
            case 'tag': {
                const allTasksForTag = filteredData?.isGrouped ? filteredData.groups?.flatMap(group => group.tasks) || [] : filteredData?.data || filteredData || [];

                const tagSet = new Set();
                allTasksForTag?.forEach(task => {
                    const taskTags = Array.isArray(task.tags) ? task.tags : task?.tag_repr ? [task.tag_repr] : [];
                    taskTags.forEach(tagName => {
                        if (tagName && !tagSet.has(tagName)) {
                            tagSet.add(tagName);
                        }
                    });
                });

                return Array.from(tagSet).map(name => ({ value: name, label: name }));
            }
            case 'workstream':
                const allTasksForWorkstream = filteredData?.isGrouped ? filteredData.groups?.flatMap(group => group.tasks) || [] : filteredData?.data || filteredData || [];
                return (
                    allTasksForWorkstream?.reduce((acc, task) => {
                        if (task.workstream_repr && !acc.find(ws => ws.value === task.workstream_repr)) {
                            acc.push({ value: task.workstream_repr, label: task.workstream_repr });
                        }
                        return acc;
                    }, []) || []
                );
            case 'project_phase': {
                const allTasksForPhase = Array.isArray(taskData) ? taskData : [];
                const phaseSet = new Set();
                (projectPhases || []).forEach(phase => {
                    if (phase?.name) {
                        phaseSet.add(phase.name);
                    }
                });
                allTasksForPhase.forEach(task => {
                    if (task.project_phase && !phaseSet.has(task.project_phase)) {
                        phaseSet.add(task.project_phase);
                    }
                });
                return Array.from(phaseSet).map(name => ({ value: name, label: name }));
            }
            case 'duration': {
                const allTasks = Array.isArray(taskData) ? taskData : [];
                const durationSet = new Set();
                allTasks.forEach(task => {
                    const minutes = getDurationMinutes(task);
                    if (minutes != null) {
                        durationSet.add(formatDurationFilterLabel(minutes));
                    }
                });
                return Array.from(durationSet).map(label => ({ value: label, label }));
            }
            case 'mode':
                return [
                    { value: 'FS', label: 'FS — Finish → Start' },
                    { value: 'SS', label: 'SS — Start → Start' },
                    { value: 'FF', label: 'FF — Finish → Finish' },
                    { value: 'SF', label: 'SF — Start → Finish' },
                ];
            default:
                return [];
        }
    };

    const hasActiveFilters = React.useMemo(
        () =>
            filters.some(filter => {
                if (!filter.column) return false;
                if (Array.isArray(filter.value)) return filter.value.length > 0;
                return !!filter.value;
            }),
        [filters],
    );

    return (
        <div className={styles.viewHeight}>
            <div className={`d-flex align-items-center justify-content-between ${styles.stickyBulkToolbar}`}>
                <div className="d-flex align-items-center gap-4">
                    <h4 className={styles.pageTitle + ' mb-0'}>{showReporting ? 'Project Status Report' : 'Task Management'}</h4>
                    {!showReporting && (
                        <span className={styles.projectStatusReportButton} onClick={() => setShowReporting(true)} role="button">
                            <GridKanban20Regular className="me-1" />
                            Back to Project Status
                        </span>
                    )}
                </div>
                <div className="d-flex gap-4">
                    {showReporting ? (
                        <span className={styles.linkText} onClick={() => setShowReporting(false)} style={{ cursor: 'pointer' }}>
                            <TaskListSquareLtr24Regular className="me-1" />
                            Task Management
                        </span>
                    ) : (
                        <>
                            {selectedRows?.length > 0 ? (
                                <div style={{ marginBottom: 8 }} className="d-flex align-items-center gap-2">
                                    <BulkOwnerUpdateMenu selectedRows={selectedRows} onBulkUpdateComplete={handleBulkOwnerUpdate} />
                                    <BulkPriorityUpdateMenu selectedRows={selectedRows} onBulkUpdateComplete={handleBulkPriorityUpdate} />
                                    <BulkWorkstreamUpdateMenu selectedRows={selectedRows} onBulkUpdateComplete={handleBulkWorkstreamUpdate} taskData={taskData} />
                                    <BulkTagUpdateMenu selectedRows={selectedRows} onBulkUpdateComplete={handleBulkTagUpdate} taskData={taskData} />
                                    <BulkStatusUpdateMenu selectedRows={selectedRows} onStatusSelect={handleBulkStatusSelect} />

                                    <BulkDeadlineUpdateMenu
                                        selectedRows={selectedRows}
                                        onBulkUpdateComplete={handleBulkDeadlineUpdate}
                                        onRequestBulkDeadlineChange={handleBulkDeadlineRequest}
                                        schedulingEnabled={isSchedulingMode(selectedProject)}
                                    />
                                    <div
                                        className={styles.bulkUpdateMenuFilled}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setShareUpdateOpen(true)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setShareUpdateOpen(true);
                                            }
                                        }}>
                                        Share Update
                                    </div>
                                    <Menu>
                                        <MenuTrigger disableButtonEnhancement>
                                            <span
                                                className={styles.linkText}
                                                style={{
                                                    cursor: exportCSVLoading || exportPDFLoading ? 'not-allowed' : 'pointer',
                                                    opacity: exportCSVLoading || exportPDFLoading ? 0.6 : 1,
                                                }}>
                                                Export ({selectedRows.length})
                                            </span>
                                        </MenuTrigger>
                                        <MenuPopover>
                                            <MenuList>
                                                <MenuItem onClick={handleExportSelectedCSV} disabled={exportCSVLoading}>
                                                    Export as CSV
                                                </MenuItem>
                                                <MenuItem onClick={handleExportSelectedPDF} disabled={exportPDFLoading}>
                                                    Export as PDF
                                                </MenuItem>
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>
                                    <DoubleConfirmationModal
                                        message="Deleting tasks will permanently remove them and all associated comments from the Project."
                                        handleAction={handleDeleteSelected}>
                                        <span className={styles.linkText}> Delete</span>
                                    </DoubleConfirmationModal>
                                </div>
                            ) : (
                                <>
                                    {' '}
                                    <div className="d-flex align-items-center gap-2">
                                        {!showReporting && hasActiveFilters && (
                                            <div className="me-2" role="button">
                                                <button
                                                    type="button"
                                                    onClick={handleClearAllFilters}
                                                    style={{
                                                        border: '1px solid var(--Brand-Secondary)',
                                                        borderRadius: '4px',
                                                        color: 'var(--Brand-Secondary)',
                                                        padding: '4px 12px',
                                                        background: 'transparent',
                                                        fontSize: 14,
                                                        cursor: 'pointer',
                                                    }}>
                                                    Filters Applied <span style={{ marginLeft: 4, fontSize: 16 }}>×</span>
                                                </button>
                                            </div>
                                        )}
                                        <Menu>
                                            <MenuTrigger>
                                                <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                                                    <Filter20Regular className="me-1" />
                                                    Filter (
                                                    {
                                                        filters.filter(f => {
                                                            if (!f.column) return false;
                                                            if (Array.isArray(f.value)) return f.value.length > 0;
                                                            return !!f.value;
                                                        }).length
                                                    }
                                                    )
                                                </span>
                                            </MenuTrigger>
                                            <MenuPopover style={{ width: '280px', minWidth: '280px' }}>
                                                <MenuList>
                                                    <div className="p-2">
                                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                                            <h6 className={styles.tableFilterHeader + ' mb-0'}>Filter Items By</h6>
                                                            {filters.some(filter => {
                                                                if (!filter.column) return false;
                                                                if (Array.isArray(filter.value)) return filter.value.length > 0;
                                                                return !!filter.value;
                                                            }) && (
                                                                <span className={styles.secondaryBtnText} onClick={handleClearAllFilters}>
                                                                    Clear All
                                                                </span>
                                                            )}
                                                        </div>

                                                        {filters.map(filter => {
                                                            const isTagFilter = filter.column === 'tag';
                                                            const valueOptions = getValueOptions(filter.column);

                                                            return (
                                                                <div key={filter.id} className="d-flex align-items-center gap-2 mb-2">
                                                                    <select
                                                                        className={'form-select form-select-sm ' + styles.filterSelect}
                                                                        value={filter.column}
                                                                        onChange={e => handleUpdateFilter(filter.id, 'column', e.target.value)}>
                                                                        <option value="">Select Column</option>
                                                                        {getColumnOptions().map(option => (
                                                                            <option key={option.value} value={option.value}>
                                                                                {option.label}
                                                                            </option>
                                                                        ))}
                                                                    </select>

                                                                    {isTagFilter ? (
                                                                        <TagFilterMenu
                                                                            value={filter.value || []}
                                                                            onChange={tags => handleUpdateFilter(filter.id, 'value', tags)}
                                                                            // Use full taskData rather than filteredData so
                                                                            // the tag list is not narrowed by current filters.
                                                                            taskData={taskData}
                                                                        />
                                                                    ) : (
                                                                        <select
                                                                            className={'form-select form-select-sm ' + styles.filterSelect}
                                                                            value={filter.value || ''}
                                                                            onChange={e => handleUpdateFilter(filter.id, 'value', e.target.value)}
                                                                            disabled={!filter.column}>
                                                                            <option value="">Filter</option>
                                                                            {valueOptions.map(option => (
                                                                                <option key={option.value} value={option.value}>
                                                                                    {option.label}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    )}

                                                                    {filters.length > 1 && (
                                                                        <button
                                                                            className="btn btn-link p-0"
                                                                            onClick={() => handleRemoveFilter(filter.id)}
                                                                            style={{ color: '#6c757d' }}>
                                                                            <Delete16Regular style={{ width: '16px', height: '16px' }} />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}

                                                        <span className={styles.secondaryBtnText + ' ms-1'} onClick={handleAddFilter}>
                                                            Add another filter
                                                        </span>
                                                    </div>
                                                </MenuList>
                                            </MenuPopover>
                                        </Menu>
                                    </div>
                                    <Menu>
                                        <MenuTrigger>
                                            <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                                                <ArrowSort20Regular className="me-1" />
                                                Sort
                                            </span>
                                        </MenuTrigger>
                                        <MenuPopover style={{ width: '280px', minWidth: '280px' }}>
                                            <MenuList>
                                                <div className="p-2">
                                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                                        <h6 className={styles.tableFilterHeader + ' mb-0'}>Sort Items By</h6>
                                                        {(sortColumn || sortOrder) && (
                                                            <span className={styles.secondaryBtnText} onClick={handleClearSort}>
                                                                Clear
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="d-flex align-items-center gap-2">
                                                        <select
                                                            className={'form-select form-select-sm ' + styles.filterSelect}
                                                            value={sortColumn}
                                                            onChange={e => setSortColumn(e.target.value)}>
                                                            <option value="">Select column</option>
                                                            {getSortColumnOptions().map(option => (
                                                                <option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </select>

                                                        <select
                                                            className={'form-select form-select-sm ' + styles.filterSelect}
                                                            value={sortOrder}
                                                            onChange={e => setSortOrder(e.target.value)}
                                                            disabled={!sortColumn}>
                                                            <option value="">Select sorting</option>
                                                            {getSortOrderOptions().map(option => (
                                                                <option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>
                                    <Menu>
                                        <MenuTrigger>
                                            <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                                                <Grid20Regular className="me-1" />
                                                Group by
                                            </span>
                                        </MenuTrigger>
                                        <MenuPopover style={{ width: '280px', minWidth: '280px' }}>
                                            <MenuList>
                                                <div className="p-2">
                                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                                        <h6 className={styles.tableFilterHeader + ' mb-0'}>Group Items By</h6>
                                                        {(groupByColumn || groupBySort) && (
                                                            <span className={styles.secondaryBtnText} onClick={handleClearGroupBy}>
                                                                Clear
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="d-flex align-items-center gap-2">
                                                        <select
                                                            className={'form-select form-select-sm ' + styles.filterSelect}
                                                            value={groupByColumn}
                                                            onChange={e => dispatch(setGroupByColumn(e.target.value))}>
                                                            <option value="">Select column</option>
                                                            <option value="owner">Owner</option>
                                                            <option value="status">Status</option>
                                                            <option value="priority">Priority</option>
                                                            <option value="deadline">Deadline</option>
                                                            <option value="project_phase">Project Phase</option>
                                                            <option value="duration">Duration</option>
                                                            {/* <option value="tag">Tag</option> */}
                                                            <option value="workstream">Workstream</option>
                                                        </select>

                                                        <select
                                                            className={'form-select form-select-sm ' + styles.filterSelect}
                                                            value={groupBySort}
                                                            onChange={e => dispatch(setGroupBySort(e.target.value))}
                                                            disabled={!groupByColumn}>
                                                            <option value="">Select Sorting</option>
                                                            <option value="asc">Ascending</option>
                                                            <option value="desc">Descending</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>
                                    <Menu>
                                        <MenuTrigger>
                                            <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                                                <EyeOff20Regular className="me-1" />
                                                Hide
                                            </span>
                                        </MenuTrigger>
                                        <MenuPopover style={{ width: '200px', minWidth: '200px' }}>
                                            <MenuList>
                                                <div className="p-2">
                                                    <h6 className={styles.tableFilterHeader + ' mb-3'}>Display Columns</h6>

                                                    {getColumnVisibilityOptions().map(option => (
                                                        <div key={option.value} className="d-flex align-items-center mb-2">
                                                            <input
                                                                type="checkbox"
                                                                style={{
                                                                    width: '16px',
                                                                    height: '16px',
                                                                    marginRight: '8px',
                                                                    cursor: 'pointer',
                                                                }}
                                                                checked={option.value === 'showAll' ? showAllColumns : isColumnVisible(option.value)}
                                                                onChange={e => handleColumnVisibility(option.value, e.target.checked)}
                                                            />
                                                            <span className={styles.tableRegularText}>{option.label}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>
                                    <div className="d-flex align-items-center">
                                        <button className={styles.regularSecondaryBtn} onClick={handleAddNewTask}>
                                            Add New Task
                                        </button>
                                        <Menu>
                                            <MenuTrigger>
                                                <button className={styles.menuButton}>
                                                    <ChevronDown12Regular />
                                                </button>
                                            </MenuTrigger>
                                            <MenuPopover>
                                                <MenuList>
                                                    <MenuItem onClick={handleExportCSV} disabled={exportCSVLoading}>
                                                        Export Tasks as CSV
                                                    </MenuItem>
                                                    <MenuItem onClick={handleExportPDF} disabled={exportPDFLoading}>
                                                        Export Tasks as PDF
                                                    </MenuItem>
                                                    <MenuDivider />
                                                    <MenuItem onClick={handleImportCSV} disabled={uploadCSVBtnLoading}>
                                                        Import Tasks from CSV
                                                    </MenuItem>
                                                    <MenuDivider />
                                                    <MenuItem onClick={handleImportTechDDTasks} disabled={uploadCSVBtnLoading}>
                                                        Import Tech DD Tasks
                                                    </MenuItem>
                                                    <MenuDivider />
                                                    <MenuItem onClick={handleDownloadTemplate}>Download CSV Template</MenuItem>
                                                </MenuList>
                                            </MenuPopover>
                                        </Menu>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* CSV column‑mapping panel (shown after user selects a CSV file) */}
            {csvFileForMapping && csvHeaders.length > 0 && (
                <div
                    className="modal fade show"
                    style={{
                        display: 'block',
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    }}
                    role="dialog"
                    aria-modal="true">
                    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Map CSV columns to task fields</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={handleCancelCsvImport} />
                            </div>
                            <div className="modal-body">
                                <div className="mb-2" style={{ fontSize: 12, color: '#6c757d' }}>
                                    File: {csvFileForMapping.name}
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-sm mb-2">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '40%' }}>Task field</th>
                                                <th>CSV column</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[
                                                { key: 'task', label: 'Task (required)' },
                                                { key: 'duration', label: 'Duration (15min, 1h, 1d, 1w)' },
                                                { key: 'mode', label: 'Mode (FS, SS, FF, SF)' },
                                                { key: 'start_date', label: 'Start Date' },
                                                { key: 'end_date', label: 'End Date' },
                                                { key: 'deadline', label: 'Deadline' },
                                                { key: 'status', label: 'Status' },
                                                { key: 'tag_repr', label: 'Tag' },
                                                { key: 'workstream_repr', label: 'Workstream' },
                                                { key: 'project_phase', label: 'Project Phase' },
                                                { key: 'task_id', label: 'Row ID (task_id)' },
                                                { key: 'parent_task_id', label: 'Parent Row ID (parent_task_id)' },
                                            ].map(field => (
                                                <tr key={field.key}>
                                                    <td>{field.label}</td>
                                                    <td>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={csvMapping[field.key] || ''}
                                                            onChange={e => handleChangeCsvMapping(field.key, e.target.value)}>
                                                            <option value="">Not mapped</option>
                                                            {csvHeaders.map(header => (
                                                                <option key={header} value={header}>
                                                                    {header}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleCancelCsvImport}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-primary btn-sm" onClick={handleConfirmCsvImport} disabled={uploadCSVBtnLoading}>
                                    Import CSV
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Hidden file input for CSV upload */}
            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileUpload} className="d-none" id="csv-upload-task-management" disabled={uploadCSVBtnLoading} />

            {showReporting ? (
                <Reporting onOpenSidebar={handleOpenSidebar} />
            ) : (
                <>
                    {!(filteredData?.isGrouped && filteredData.groups?.length) && (
                        <div className="mt-3 mb-2">
                            <div className="d-flex align-items-center justify-content-between">
                                <span className={styles.todoText} onClick={handleToggleTable} style={{ cursor: 'pointer' }}>
                                    {!tableCollapsed ? (
                                        <ChevronDown20Regular className="me-1" />
                                    ) : (
                                        <ChevronDown20Regular className="me-1" style={{ transform: 'rotate(-90deg)' }} />
                                    )}
                                    To-do
                                </span>
                            </div>
                        </div>
                    )}
                    {(!tableCollapsed || (filteredData?.isGrouped && filteredData.groups?.length > 0)) && (
                        <TasksTable
                            filteredData={filteredData}
                            setFilteredData={setFilteredData}
                            projectUsersAll={projectUsersAll}
                            modalVisible={modalVisible}
                            setModalVisible={setModalVisible}
                            selectedRow={selectedRow}
                            setSelectedRow={setSelectedRow}
                            isAddingNewTask={isAddingNewTask}
                            setIsAddingNewTask={setIsAddingNewTask}
                            newTaskText={newTaskText}
                            setNewTaskText={setNewTaskText}
                            selectedProject={selectedProject}
                            isColumnVisible={isColumnVisible}
                            onOpenSidebar={handleOpenSidebar}
                            selectedRows={selectedRows}
                            setSelectedRows={setSelectedRows}
                            groupByColumn={groupByColumn}
                            collapsedGroups={collapsedGroups}
                            onToggleGroup={handleToggleGroup}
                            onDragStart={handleDragStart}
                            onDragMove={handleDragMove}
                            onDragEnd={handleDragEnd}
                            onDragCancel={handleDragCancel}
                            dropIntent={dropIntent}
                            activeDragId={activeDragId}
                            customOrder={customOrder}
                            sensors={sensors}
                            expandedTasks={expandedTasks}
                            setExpandedTasks={setExpandedTasks}
                            addNewTaskFocusTrigger={addNewTaskFocusTrigger}
                            sortColumn={sortColumn}
                            sortOrder={sortOrder}
                            previewTaskId={previewTaskId}
                            setPreviewTaskId={setPreviewTaskId}
                            onRequestSort={columnKey => {
                                // Switching to column sort should override any custom drag order
                                setCustomOrder(null);

                                setSortColumn(prevCol => {
                                    const isSame = prevCol === columnKey;
                                    // Toggle order when clicking same column, otherwise default to newest-first for date columns
                                    setSortOrder(prevOrder => {
                                        if (isSame) return prevOrder === 'desc' ? 'asc' : 'desc';
                                        if (columnKey === 'lastUpdated' || columnKey === 'addedOn' || columnKey === 'timeline' || columnKey === 'deadline') return 'desc';
                                        return 'asc';
                                    });
                                    return columnKey;
                                });
                            }}
                        />
                    )}
                </>
            )}

            {/* Task Details Sidebar */}
            <TaskDetailsSidebar
                open={sidebarOpen}
                onClose={handleCloseSidebar}
                task={selectedTaskForSidebar}
                focusRequest={sidebarFocusRequest}
                onFocusRequestHandled={() => setSidebarFocusRequest(null)}
                onRequestTimelineChange={handleRequestTimelineChange}
                onRequestDurationChange={handleRequestDurationChange}
                onRequestModeChange={handleRequestModeChange}
                onRequestDeadlineChange={handleRequestDeadlineChange}
                onRequestStatusChange={handleRequestStatusChange}
                customOrder={customOrder}
                hasPrev={(function () {
                    const allTasks = filteredData?.isGrouped ? filteredData.groups?.flatMap(group => group.tasks) || [] : filteredData?.data || filteredData || [];
                    if (!selectedTaskForSidebar) return false;
                    const currentIndex = allTasks.findIndex(t => t.id === selectedTaskForSidebar.id);
                    return currentIndex > 0;
                })()}
                hasNext={(function () {
                    const allTasks = filteredData?.isGrouped ? filteredData.groups?.flatMap(group => group.tasks) || [] : filteredData?.data || filteredData || [];
                    if (!selectedTaskForSidebar) return false;
                    const currentIndex = allTasks.findIndex(t => t.id === selectedTaskForSidebar.id);
                    return currentIndex !== -1 && currentIndex < allTasks.length - 1;
                })()}
                onNavigateTask={direction => {
                    const allTasks = filteredData?.isGrouped ? filteredData.groups?.flatMap(group => group.tasks) || [] : filteredData?.data || filteredData || [];
                    if (!selectedTaskForSidebar) return;
                    const currentIndex = allTasks.findIndex(t => t.id === selectedTaskForSidebar.id);
                    if (currentIndex === -1) return;
                    if (direction === 'prev' && currentIndex > 0) {
                        setSelectedTaskForSidebar(allTasks[currentIndex - 1]);
                    } else if (direction === 'next' && currentIndex < allTasks.length - 1) {
                        setSelectedTaskForSidebar(allTasks[currentIndex + 1]);
                    }
                }}
            />
            <ShareUpdateSidebar
                open={shareUpdateOpen}
                onClose={() => setShareUpdateOpen(false)}
                selectedRows={selectedRows}
                taskData={taskData}
                selectedProject={selectedProject}
                projectUsersAll={projectUsersAll}
                customOrder={customOrder}
            />
            <WarningConfirmModal
                open={Boolean(pendingHierarchyMove)}
                onClose={handleHierarchyMoveCancel}
                onConfirm={handleHierarchyMoveContinue}
                confirmLoading={hierarchyMoveLoading}
                message="Changing the task hierarchy can recalculate derived Timeline, Duration, Status and scheduling relationships. Parent values will be calculated from their subtasks."
            />
            <RecalculateScheduleModal
                open={Boolean(pendingScheduleChange)}
                onClose={() => !scheduleChangeLoading && setPendingScheduleChange(null)}
                onUpdateOnly={() => applyScheduleChange({ updateOnly: true })}
                onRecalculateDownstream={({ adjustDeadlines } = {}) => applyScheduleChange({ updateOnly: false, adjustDownstreamDeadlines: Boolean(adjustDeadlines) })}
                fieldLabel={pendingScheduleChange?.fieldLabel || 'this task'}
                affectedCount={pendingScheduleChange?.affectedCount}
                confirmLoading={scheduleChangeLoading}
            />
            <AdjustDeadlineModal
                open={Boolean(pendingDeadlineChange)}
                onClose={() => !deadlineChangeLoading && setPendingDeadlineChange(null)}
                onChangeDeadlineOnly={() => applyDeadlineChange({ adjustTimelineToDeadline: false })}
                onAdjustTimelineToDeadline={() => applyDeadlineChange({ adjustTimelineToDeadline: true })}
                canAdjustTimeline={pendingDeadlineChange?.canAdjustTimeline !== false}
                confirmLoading={deadlineChangeLoading}
            />
            <PauseTaskConfirmModal
                open={Boolean(pendingStatusChange)}
                onClose={() => !statusChangeLoading && setPendingStatusChange(null)}
                onThisOnly={handleStatusThisOnly}
                onWithFollowing={handleStatusWithFollowing}
                confirmLoading={statusChangeLoading}
                action={pendingStatusChange?.action || 'pause'}
            />
            <PauseTaskConfirmModal
                open={Boolean(pendingBulkStatusChange)}
                onClose={() => !bulkStatusChangeLoading && setPendingBulkStatusChange(null)}
                onThisOnly={handleBulkStatusThisOnly}
                onWithFollowing={handleBulkStatusWithFollowing}
                confirmLoading={bulkStatusChangeLoading}
                action={pendingBulkStatusChange?.action || 'pause'}
                selectedCount={pendingBulkStatusChange?.taskIds?.length}
            />
        </div>
    );
};

export const CustomAvatar = ({ name, size = 16, color = '#1B2149', margin = 'auto' }) => {
    const getInitials = fullName => {
        if (!fullName) return '';
        const names = fullName.split(' ');
        if (names.length >= 2) {
            return (names[0][0] + names[1][0]).toUpperCase();
        }
        return fullName[0]?.toUpperCase() || '';
    };

    return (
        <div
            className={styles.customAvatar}
            style={{
                width: size,
                height: size,
                backgroundColor: color,
                fontSize: '9px',
                margin: margin || 'auto',
            }}
            title={name}>
            {getInitials(name)}
        </div>
    );
};

export const ProgressCircle = ({ value }) => {
    const size = 14; // fixed size 16px
    const strokeWidth = 1; // thickness of the progress ring

    // circle math
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (value / 100) * circumference;

    return (
        <svg width={size} height={size} style={{ display: 'block' }} role="progressbar" aria-valuenow={value} aria-valuemin="0" aria-valuemax="100">
            {/* background circle */}
            {/* <circle stroke="#def" fill="transparent" strokeWidth={strokeWidth} r={radius} cx={size / 2} cy={size / 2} /> */}
            {/* progress circle */}
            <circle
                stroke="var(--Neutral-Gray-6)"
                fill="transparent"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                r={radius}
                cx={size / 2}
                cy={size / 2}
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                style={{ transition: 'stroke-dashoffset 0.5s ease' }}
            />
        </svg>
    );
};

export default TaskManagement;
